"use client";
import get from "lodash/get";
import pick from "lodash/pick";
import isEmpty from "lodash/isEmpty";
import mapKeys from "lodash/mapKeys";
import { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import useRouter from "@stadium/hooks/lib/use-custom-route";

import { useAuth0V2 } from "@stadium/services/auth0";
import { JuneSoContext, useJuneSoPageTracker } from "@stadium/services/june-analytics";
import { userIdTracker } from "@stadium/services/google-analytics";
import { MixpanelContext } from "@stadium/services/mixpanel-analytics";

import useFetchCurrentUserData from "@stadium/hooks/lib/use-current-user/use-fetch-current-user-data";
import { decodeToken } from "@stadium/helpers/token";

import {
  CurrentUserContext,
  USER_DATA_FIELDS,
  defaultCurrentUserData,
} from "@stadium/hooks/lib/use-current-user";

import { setSentryUser } from "@stadium/services/error-reporting";
import { useQuery, useAuthQuery } from "@stadium/services/apollo-client";

import { selectAllCountries } from "@stadium/redux-utils/selectors/configSelector";
import { useConfig } from "@stadium/hooks/lib/use-config";
import { updateUserGeolocationParams } from "@stadium/hooks/api/user";
import { useAuthContext } from "@stadium/hooks/lib/use-auth-wrapper";
import usePathMatcher from "@stadium/hooks/lib/use-path-matcher";

import {
  GetUserConfigDocument,
  FetchRewardUserDocument,
} from "@stadium/shared/src/graphql/operations";

import { getCountryDataFromName } from "@stadium/helpers/country/utils";

import { IS_AUTH0_ENABLED } from "@stadium/services/auth0/constants";
import { DEFAULT_WORKSPACE_ROUTE } from "@stadium/constants";

const DEFAULT_OBJ = {};
const DEFAULT_ARR = [];

const CurrentUserProvider = ({
  children,
  configRequired = true,
  shouldSkipWorkspaceValidationPaths = DEFAULT_ARR,
}) => {
  const shouldSkipWorkspaceValidation = usePathMatcher(shouldSkipWorkspaceValidationPaths);

  const router = useRouter();

  const dispatch = useDispatch();
  const [currentUserData, setCurrentUserData] = useState(defaultCurrentUserData);
  const [isSSOEnabled, setIsSSOEnabled] = useState(false);
  const { setCurrentUserData: setUserData, setIsUserDataLoaded } = useContext(JuneSoContext);
  const {
    setCurrentUserData: setMixpanelUserData,
    setIsUserDataLoaded: setIsMixpanelUserDataLoaded,
  } = useContext(MixpanelContext);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0V2();
  const { axiosInterceptorAdded, graphqlInterceptorAdded } = useAuthContext();
  const { data, error, loading, refetch, called } = useFetchCurrentUserData();
  const { countryName, contactProviders } = data?.currentUser || {};
  const countries = useConfig(selectAllCountries);

  const { data: rewardUserData } = useAuthQuery(FetchRewardUserDocument);

  const userId = get(data, "currentUser.spreeId");

  const childCompany = get(data, "currentUser.childCompanies[0]", DEFAULT_OBJ);
  const activePackage = childCompany?.activePackage || DEFAULT_OBJ;
  const permissions = get(data, "currentUser.permissions", DEFAULT_ARR);

  const { data: configData, refetch: refetchUserConfigData } = useQuery(GetUserConfigDocument, {
    variables: {
      shouldSkipWorkspaceValidation,
    },
    skip: !isAuthenticated || !axiosInterceptorAdded || !graphqlInterceptorAdded || !configRequired,
  });

  useJuneSoPageTracker({ isAuthenticated, isFetchingUser: loading });

  useEffect(() => {
    if (userId) {
      userIdTracker(userId);
      setSentryUser?.({ id: userId });
    }
  }, [userId]);

  useEffect(() => {
    if (called && !loading && !error) {
      const response = get(data, "currentUser");
      const userData = pick(response, USER_DATA_FIELDS);
      const isSSOEnabled = response?.isSsoEnabled;

      setIsSSOEnabled(isSSOEnabled);

      const { firstName, lastName } = userData;
      const name = [firstName, lastName].filter(Boolean).join(" ");
      setCurrentUserData({ ...userData, name, teams: data?.teams });

      if (!isEmpty(userData)) {
        setUserData({ ...userData, name });
        setIsUserDataLoaded(true);
        if (setIsMixpanelUserDataLoaded) {
          setMixpanelUserData({ ...userData, name });
          setIsMixpanelUserDataLoaded(true);
        }
      }
    } else if (error) {
      const errMessage = get(error, "networkError.result.error", "");
      const statusCode = get(error, "networkError.statusCode", 500);

      if (statusCode === 401 && errMessage == "Unauthorized request.") {
        router.replace(DEFAULT_WORKSPACE_ROUTE);
      }
    }
  }, [loading, error, data, called]);

  //set user's default country details from ipStack
  useEffect(() => {
    if (isAuthenticated && !loading) {
      setTimeout(() => {
        dispatch(updateUserGeolocationParams(countryName));
      }, 2000);
    }
  }, [countryName, isAuthenticated, loading, dispatch]);

  const ldData = useMemo(() => {
    const userKey = get(data, "currentUser.ldUserKey");
    const secureModeHash = get(data, "currentUser.ldSecureModeHash");

    return { userKey, secureModeHash };
  }, [data]);

  const baseCountry = useMemo(
    () => getCountryDataFromName(countries, countryName),
    [countryName, countries],
  );

  useEffect(() => {
    if (!IS_AUTH0_ENABLED) return;
    getAccessTokenSilently()
      .then((token) => decodeToken(token))
      .then((data) => {
        const isSSOEnabled = data["https://bystadium.com/oauth/claims/ssoEnabled"];
        setIsSSOEnabled(isSSOEnabled);
      })
      .catch((error) => {
        setIsSSOEnabled(false);
        // eslint-disable-next-line no-console
        console.log(error);
      });
  }, [getAccessTokenSilently, axiosInterceptorAdded, graphqlInterceptorAdded]);

  const contactProvidersDetails = useMemo(
    () => mapKeys(contactProviders || [], ({ category }) => category.toLowerCase()),
    [contactProviders],
  );

  const value = useMemo(() => {
    return {
      loading: loading || (isAuthenticated && !currentUserData.email),
      currentUserData,
      activePackage,
      permissions,
      childCompany,
      currentUserConfig: configData?.user,
      baseCountry,
      setCurrentUserData,
      ldData,
      refetch,
      isSSOEnabled,
      contactProvidersDetails,
      stadiumPoints: rewardUserData?.currentUser?.rewardPoints || 0,
      refetchUserConfigData,
      shouldSkipWorkspaceValidation,
    };
  }, [
    loading,
    isAuthenticated,
    currentUserData,
    baseCountry,
    activePackage,
    permissions,
    childCompany,
    setCurrentUserData,
    ldData,
    refetch,
    configData,
    isSSOEnabled,
    contactProvidersDetails,
    rewardUserData,
    refetchUserConfigData,
    shouldSkipWorkspaceValidation,
  ]);

  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
};

export { useCurrentUserContext } from "@stadium/hooks/lib/use-current-user";
export default CurrentUserProvider;
